import {createEvent} from "./utils/common";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import dayOfYear from "dayjs/plugin/dayOfYear";
import isLeapYear from "dayjs/plugin/isLeapYear";
import "dayjs/locale/ru";
import {FlatpickrFn} from "flatpickr/dist/types/instance";
import {DateOption} from "flatpickr/dist/types/options";

// TODO: Добавить типы

dayjs.locale(`ru`);
dayjs.extend(weekday);
dayjs.extend(dayOfYear);
dayjs.extend(isLeapYear);

const Period = {
  TODAY: `today`,
  YESTERDAY: `yesterday`,
  MONTH: `month`,
  CURRENT_WEEK: `currentWeek`,
  PREVIOUS_WEEK: `prevWeek`,
  CURRENT_MONTH: `currentMonth`,
  PREVIOUS_MONTH: `prevMonth`,
  CURRENT_YEAR: `currentYear`,
  PREVIOUS_YEAR: `prevYear`,
  ALL_PERIOD: `allPeriod`,
  SEVEN_DAYS: `7days`,
};

const lastDayOfMonth = {
  0: 31,
  1: dayjs()
    .isLeapYear() ? 29 : 28,
  2: 31,
  3: 30,
  4: 31,
  5: 30,
  6: 31,
  7: 31,
  8: 30,
  9: 31,
  10: 30,
  11: 31,
};

const LastWeekShift = {
  START: -7,
  END: -1,
};

const DownloadValue = {
  ENABLE: `1`,
  DISABLE: `0`,
};

const DAY_SHIFT = 1;
const FIRST_DAY = 1;
const DAYS_FOR_START_SEVEN_DAYS = 6;
const LAST_MONTH = 11;

class ReportDate {
  private formElement: HTMLFormElement;
  private calendarElement;
  private startDateWrapperElement;
  private endDateWrapperElement;
  private openStartCalendarElement;
  private openEndCalendarElement;
  private calendar;
  private startDateInputElement;
  private endDateInputElement;
  private periodInputElement;
  private readonly resetDateButtonElement;
  private readonly resetPeriodButtonElement;
  private downloadEnableInputElement: HTMLInputElement;
  private downloadSubmitButtonElement: HTMLButtonElement;
  private submitButtonElement: HTMLButtonElement;

  private startProjectDate: number;

  private isSent = false;

  private timeStampNow = new Date();
  flatpickr: FlatpickrFn;

  constructor(flatpickr: FlatpickrFn, container: HTMLFormElement) {
    this.flatpickr = flatpickr;
    this.formElement = container;

    this.startProjectDate = this.formElement.dataset.startPeriod ? +this.formElement.dataset.startPeriod * 1000 : 0;

    this.calendarElement = this.formElement.querySelector(`.js-calendar`);
    this.startDateWrapperElement = this.formElement.querySelector(`.js-report-start-date`);
    this.endDateWrapperElement = this.formElement.querySelector(`.js-report-end-date`);
    this.openStartCalendarElement = this.startDateWrapperElement.querySelector(`.js-open-start-calendar`);
    this.openEndCalendarElement = this.endDateWrapperElement.querySelector(`.js-open-end-calendar`);
    this.startDateInputElement = this.formElement.querySelector(`.js-start-calendar`);
    this.endDateInputElement = this.formElement.querySelector(`.js-end-calendar`);
    this.periodInputElement = this.formElement.querySelector(`.js-period-input`);
    this.resetDateButtonElement = this.formElement.querySelector(`.js-date-reset`);
    this.resetPeriodButtonElement = this.formElement.querySelector(`.js-period-reset`);
    this.downloadEnableInputElement = this.formElement.querySelector(`.js-download-enabled`)!;
    this.downloadSubmitButtonElement = this.formElement.querySelector(`.js-download-submit`)!;
    this.submitButtonElement = this.formElement.querySelector(`.js-submit`)!;

    this.onOpenCalendarElementClick = this.onOpenCalendarElementClick.bind(this);
    this.onPeriodInputElementChange = this.onPeriodInputElementChange.bind(this);
    this.onResetDateElementClick = this.onResetDateElementClick.bind(this);
    this.onResetPeriodElementClick = this.onResetPeriodElementClick.bind(this);
    this.onDownloadSubmitButtonClick = this.onDownloadSubmitButtonClick.bind(this);
    this.onSubmitButtonClick = this.onSubmitButtonClick.bind(this);
  }

  init() {
    this.addFlatpickrCalendars();
    this.setOpenEndCalendarClickHandler();
    this.setOpenStartCalendarClickHandler();
    this.setPeriodInputElementChangeHandler();
    this.setResetDateElementClickHandler();
    this.setResetPeriodElementClickHandler();
    this.setDownloadSubmitButtonClickHandler();
    this.setSubmitButtonClickHandler();
  }

  private getFormatDateOption(date: Date): string {
    if (date.getFullYear() !== this.timeStampNow.getFullYear()) {
      return "D, d F Y";
    }

    return "D, d F";
  }

  private addFlatpickrCalendars() {
    const changeDate = (dates, instance) => {
      this.setStarDateTextContent(
        instance.formatDate(new Date(dates[0]), this.getFormatDateOption(new Date(dates[0]))));
      this.setStartHiddenCalendarDate(instance.formatDate(new Date(dates[0]), "Y-m-d"));

      if (dates[1]) {
        this.setEndDateTextContent(
          instance.formatDate(new Date(dates[1]), this.getFormatDateOption(new Date(dates[1]))));
        this.setEndHiddenCalendarDate(instance.formatDate(new Date(dates[1]), "Y-m-d"));
      }

      if (
        dates[0].toLocaleDateString() !== this.timeStampNow.toLocaleDateString() ||
        (dates[1] && dates[1].toLocaleDateString() !== this.timeStampNow.toLocaleDateString())
      ) {
        this.showResetButton(this.getResetDateElement());
      }
    };

    const defaultDates = [this.getStartDate(), this.getEndDate()];

    this.calendar = this.flatpickr(`.js-calendar`, {
      altFormat: "D, j M",
      dateFormat: "Y-m-d",
      defaultHour: 0,
      defaultMinute: 0,
      defaultDate: defaultDates,
      monthSelectorType: "static",
      static: false,
      mode: "range",
      onChange: [
        function(selectedDates, dateStr, instance) {
          changeDate(selectedDates, instance);
        },
      ],
      onReady: function(selectedDates, dateStr, instance) {
        changeDate(selectedDates, instance);
      },
    });
  }

  getCalendar() {
    return this.calendar;
  }

  getPeriodElementValue(): string {
    return this.periodInputElement.value;
  }

  getResetDateElement() {
    return this.resetDateButtonElement;
  }

  getResetPeriodElement() {
    return this.resetPeriodButtonElement;
  }

  private getStartDate() {
    return this.startDateInputElement.value;
  }

  private getEndDate() {
    return this.endDateInputElement.value;
  }

  private setStartHiddenCalendarDate(date) {
    this.startDateInputElement.value = date;
  }

  private setEndHiddenCalendarDate(date) {
    this.endDateInputElement.value = date;
  }

  private showResetButton(button) {
    button.classList.add(`select__clear--show`);
  }

  private hideResetButton(button) {
    button.classList.remove(`select__clear--show`);
  }

  private resetPeriodSelect() {
    this.periodInputElement.dispatchEvent(createEvent(`resetSelect`));
  }

  private setStarDateTextContent(text) {
    this.openStartCalendarElement.textContent = text;
  }

  private setEndDateTextContent(text) {
    this.openEndCalendarElement.textContent = text;
  }

  private setDate(dates: DateOption | DateOption[]) {
    this.getCalendar()
      .setDate(dates, true);
  }

  private setCurrentAllPeriod() {
    this.setDate([dayjs(this.startProjectDate)
      .format(), dayjs()
      .format()]);
  }

  private setCurrentYearPeriod() {
    this.setDate([dayjs()
      .dayOfYear(1)
      .format(), dayjs()
      .format()]);
  }

  private setPreviousYearPeriod() {
    const prevYear = dayjs()
      .year(dayjs()
        .year() - 1);
    this.setDate([prevYear.dayOfYear(1)
      .format(), prevYear.dayOfYear(365)
      .format()]);
  }

  private setCurrentMonthPeriod() {
    this.setDate([dayjs()
      .date(1)
      .format(), dayjs()
      .format()]);
  }

  private setPreviousMonthPeriod() {
    const prevMonth = dayjs()
      .month() - DAY_SHIFT === -1 ? LAST_MONTH : dayjs()
      .month() - DAY_SHIFT;

    const currentYear = dayjs()
      .year();

    const year = prevMonth !== LAST_MONTH ? currentYear : dayjs()
      .year(currentYear - 1)
      .year();

    const startMonthDay = dayjs()
      .year(year)
      .month(prevMonth)
      .date(FIRST_DAY)
      .format();

    const endMonthDay = dayjs()
      .year(year)
      .month(prevMonth)
      .date(lastDayOfMonth[prevMonth])
      .format();

    this.setDate([startMonthDay, endMonthDay]);
  }

  private setPreviousWeekPeriod() {
    this.setDate([dayjs()
      .weekday(LastWeekShift.START)
      .format(), dayjs()
      .weekday(LastWeekShift.END)
      .format()]);
  }

  private setCurrentWeekPeriod() {
    this.setDate([dayjs()
      .weekday(0)
      .format(), dayjs()
      .format()]);
  }

  private setSevenDaysPeriod() {
    const startDate = dayjs()
      .dayOfYear(dayjs()
        .dayOfYear() - DAYS_FOR_START_SEVEN_DAYS)
      .format();
    this.setDate([startDate, dayjs()
      .format()]);
  }

  private setTodayPeriod() {
    const today = dayjs()
      .format();
    this.setDate([today, today]);
  }

  private setYesterdayPeriod() {
    const yesterday = dayjs()
      .subtract(1, "day")
      .format();
    this.setDate([yesterday, yesterday]);
  }

  private enableDownloadReport() {
    this.downloadEnableInputElement.value = DownloadValue.ENABLE;
  }

  private disableDownloadReport() {
    this.downloadEnableInputElement.value = DownloadValue.DISABLE;
  }

  onPeriodInputElementChange() {
    const currentPeriod = this.getPeriodElementValue();

    switch (currentPeriod) {
      case Period.ALL_PERIOD:
        this.setCurrentAllPeriod();
        break;
      case Period.CURRENT_YEAR:
        this.setCurrentYearPeriod();
        break;
      case Period.PREVIOUS_YEAR:
        this.setPreviousYearPeriod();
        break;
      case Period.CURRENT_MONTH:
        this.setCurrentMonthPeriod();
        break;
      case Period.PREVIOUS_MONTH:
        this.setPreviousMonthPeriod();
        break;
      case Period.PREVIOUS_WEEK:
        this.setPreviousWeekPeriod();
        break;
      case Period.CURRENT_WEEK:
        this.setCurrentWeekPeriod();
        break;
      case Period.SEVEN_DAYS:
        this.setSevenDaysPeriod();
        break;
      case Period.TODAY:
        this.setTodayPeriod();
        break;
      case Period.YESTERDAY:
        this.setYesterdayPeriod();
        break;
    }

    this.showResetButton(this.getResetPeriodElement());
  }

  onOpenCalendarElementClick() {
    this.getCalendar()
      .open();
  }

  onResetDateElementClick() {
    this.hideResetButton(this.getResetPeriodElement());
    this.hideResetButton(this.getResetDateElement());
    this.resetPeriodSelect();
    this.setDate([this.timeStampNow, this.timeStampNow]);
  }

  onResetPeriodElementClick() {
    this.hideResetButton(this.getResetPeriodElement());
    this.hideResetButton(this.getResetDateElement());
    this.resetPeriodSelect();
    this.setDate([this.timeStampNow, this.timeStampNow]);
  }

  onDownloadSubmitButtonClick(evt) {
    evt.preventDefault();

    this.enableDownloadReport();
    this.formElement.submit();
  }

  onSubmitButtonClick(evt) {
    evt.preventDefault();

    if (this.isSent) return;

    this.isSent = true;

    this.submitButtonElement.classList.add('select__submit--sent');

    this.disableDownloadReport();
    this.formElement.submit();
  }

  private setPeriodInputElementChangeHandler() {
    this.periodInputElement.addEventListener(`change`, this.onPeriodInputElementChange);
  }

  private setOpenStartCalendarClickHandler() {
    this.openStartCalendarElement.addEventListener(`click`, this.onOpenCalendarElementClick);
  }

  private setOpenEndCalendarClickHandler() {
    this.openEndCalendarElement.addEventListener(`click`, this.onOpenCalendarElementClick);
  }

  private setResetDateElementClickHandler() {
    this.resetDateButtonElement.addEventListener(`click`, this.onResetDateElementClick);
  }

  private setResetPeriodElementClickHandler() {
    this.resetPeriodButtonElement.addEventListener(`click`, this.onResetPeriodElementClick);
  }

  private setDownloadSubmitButtonClickHandler() {
    this.downloadSubmitButtonElement.addEventListener(`click`, this.onDownloadSubmitButtonClick);
  }

  private setSubmitButtonClickHandler() {
    this.submitButtonElement.addEventListener(`click`, this.onSubmitButtonClick);
  }
}

export const report = (flatpickr) => {
  const reportContainer: HTMLFormElement | null = document.querySelector(`.js-report`);

  if (reportContainer) {
    new ReportDate(flatpickr, reportContainer).init();
  }
};
